import React from "react"
import PropTypes from "prop-types"
import { graphql, Link } from "gatsby"
import SimpleText from "components/SimpleText"
import SpeakerCard from "components/SpeakerCard"
import TrackPill from "components/TrackPill"
import TalkTime from "components/TalkTime"
import theme from "theme"

const TalkCard = ({
  title,
  slug,
  track,
  speakers,
  keynote,
  keynoteLabel,
  lede,
  description,
  time,
  duration,
  tags,
  featured,
  toBeAnnounced,
  className,
}) => (
  <div
    css={{
      display: "grid",
      gridTemplateColumns: toBeAnnounced ? "1fr" : "1fr 1fr",
      gridTemplateRows: "min-content 1fr",
      gap: 24,
      background: theme.white,
      padding: 24,
      [theme.mobile]: {
        gridTemplateColumns: "1fr",
      },
    }}
    className={className}
  >
    {!toBeAnnounced && (
      <div
        css={{
          display: "grid",
          gridTemplateColumns:
            speakers.length > 1
              ? `repeat(2, minmax(auto, ${featured ? 290 : 160}px))`
              : "1fr",
          justifyItems: "start", // keep link from expanding too far right
          justifyContent: "start", // align speaker cards to the left
          alignContent: "start", // align speaker cards to the top when there are multiple rows and the copy is long
          gap: 24,
          gridColumn: "1",
          gridRow: "1 / -1",
          [theme.mobile]: {
            gridRow: "auto",
            order: 2,
          },
        }}
      >
        {speakers.map(({ _id, ...speaker }) => (
          <SpeakerCard
            key={_id}
            {...speaker}
            variant={
              speakers.length > 1
                ? featured
                  ? "standard"
                  : "small"
                : featured
                ? "large"
                : "standard"
            }
          />
        ))}
      </div>
    )}

    {/* Keep heading section outside of the block so it can go above the speakers
        on mobile */}
    <div css={{ [theme.mobile]: { order: 1 } }}>
      {keynote && (
        <div css={{ ...theme.h1, marginBottom: 16 }}>{keynoteLabel}</div>
      )}
      {featured && !toBeAnnounced ? (
        <h1 css={theme.h1}>{title}</h1>
      ) : (
        <h3 css={theme.h2}>{title}</h3>
      )}
    </div>

    <div
      css={{
        gridColumn: !toBeAnnounced && "2",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "flex-start",
        [theme.mobile]: {
          gridColumn: "auto",
          order: 3,
        },
      }}
    >
      {!toBeAnnounced &&
        (description ? (
          <SimpleText content={description} />
        ) : (
          <div css={{ [theme.mobile]: { marginTop: 16 } }}>
            <div>{lede}</div>
            <div css={{ marginTop: 16 }}>
              <Link to={slug.current}>Read more →</Link>
            </div>
          </div>
        ))}

      <div>
        <div css={{ marginTop: 32, display: "flex", flexWrap: "wrap" }}>
          {track && (
            <TrackPill {...track} css={{ marginRight: 8, marginBottom: 8 }} />
          )}
          {tags.map(tag => (
            <div
              key={tag}
              css={{
                ...theme.hxxs,
                color: "#7E7E7E",
                border: "2px solid currentColor",
                padding: "6px 16px",
                marginRight: 8,
                marginBottom: 8,
              }}
            >
              {tag}
            </div>
          ))}
        </div>

        <TalkTime time={time} duration={duration} css={{ marginTop: 16 }} />
      </div>
    </div>
  </div>
)

export default TalkCard

TalkCard.propTypes = {
  title: PropTypes.string.isRequired,
  slug: PropTypes.shape({
    current: PropTypes.string.isRequired,
  }).isRequired,
  track: PropTypes.shape(TrackPill.propTypes),
  keynote: PropTypes.bool,
  keynoteLabel: PropTypes.node,
  lede: PropTypes.string.isRequired,
  description: PropTypes.arrayOf(PropTypes.object),
  speakers: PropTypes.arrayOf(PropTypes.shape(SpeakerCard.propTypes).isRequired)
    .isRequired,
  time: PropTypes.string.isRequired,
  duration: PropTypes.number.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string.isRequired),
  featured: PropTypes.bool,
  toBeAnnounced: PropTypes.bool,
  className: PropTypes.string,
}

export const query = graphql`
  fragment TalkCard on SanityConfigTalk {
    _id
    title
    slug {
      current
    }
    track {
      ...TrackPill
    }
    keynote
    keynoteLabel
    lede
    speakers {
      ...SpeakerCard
    }
    time
    duration
    tags
  }
`
